import { autoinject } from "aurelia-dependency-injection";
import { OfferingModel, OnboardingRequestDocumentModel, OnboardingWhatsNeededStepModel } from "../../../models/onboarding/onboarding.models";
import { OnboardingClientService } from "../../../services/onboarding/onboarding-client.service";
import { IOnboardingSessionHandler, IVerificationHandler } from "./interfaces/contracts-handlers.models";
import { RulesHolder } from "../../../client-validation-system/validations.models";
import { PropertyMetaModel } from "../../../models/basics/basics.models";
import { computedFrom } from "aurelia-binding";
import { BaseOnboardingStepModel } from "./steps/base-step-model";
import { Reflection } from "../../../utilities/reflection";
import { UrlHelper } from "../../../helpers/url-helper";

@autoinject()
export class OnboardingSession {

    private _modelMeta: any;
    private _handler: IOnboardingSessionHandler;
    private _guid: string;
    private _query: string;
    private _documents: OnboardingRequestDocumentModel[] = [];
    private _onboardingRequestGuid: string = null;
    private _offerings: OfferingModel[] = [];
    private _offeringsBags: any[] = [];
    private _basicMetaConfig: string;
    private _offeringsMetaConfig: string;
    private _pluginMetaConfig: string;
    private _landingPage: string;
    private _completeRequestLandingPage: string;
    private _onboardingRequestsPath: string;
    private _verificationHandler: IVerificationHandler;
    private _onboardingModelValidations: RulesHolder;
    private _offeringsCount: number;
    private _offeringsWithVisibleMetaCount: number;
    private _onboardingRequiredDocumentTypesCount: number;
    private _advancedMetaConfig: string;
    private _onboardingModelProperties: PropertyMetaModel[];
    private _offeringModelProperties: PropertyMetaModel[];
    private _termsAndConditions: string;
    private _pluginName: string;
    private _isBasicMetaFormStyle: boolean;
    private _isAdvancedMetaFormStyle: boolean;
    private _verificationsMeta: { [verificationName: string]: any } = {};
    private _navigationHandler: string;
    private _doNotUseFallback: boolean;
    private _onboardingName: string;
    private _isEmbedded: boolean;
    private _validationErrorsOnSubmit: boolean;
    private _isGdprConsentEnabled: boolean;
    private _whatsNeededSteps: OnboardingWhatsNeededStepModel[];
    private _entityTypeId: number;
    private _urls: string[] = [];

    public constructor(private onboardingClientService: OnboardingClientService) { }

    createSession(handler: IOnboardingSessionHandler, model: { guid: string, query: string, isEmbedded: boolean } = null): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            if (model && model.guid) {
                this._guid = model.guid;
            }
            if (model && model.query) {
                this._query = model.query;
            }
            this._isEmbedded = model ? model.isEmbedded : false;
            this._modelMeta = {};
            this._handler = handler;
            this._offeringsBags = [];
            this.onboardingClientService.getOnboardingClientConfiguration(this.guid).then(response => {

                if (!response.isValidCountry) {
                    reject({ isValidCountry: false, pluginName: response.pluginName, onboardingRequestsPath: response.onboardingRequestsPath, landingPage: response.landingPage, country: response.country });
                    return;
                }

                this._pluginName = response.pluginName;
                this._basicMetaConfig = response.basicMetaConfig;
                this._pluginMetaConfig = response.pluginMetaConfig;
                this._advancedMetaConfig = response.advancedMetaConfig;
                this._isBasicMetaFormStyle = response.isBasicMetaFormStyle;
                this._isAdvancedMetaFormStyle = response.isAdvancedMetaFormStyle;
                this._offeringsMetaConfig = response.offeringsMetaConfig;
                this._landingPage = response.landingPage;
                this._onboardingRequestsPath = response.onboardingRequestsPath;
                this._completeRequestLandingPage = response.completeRequestLandingPage;
                this._onboardingModelValidations = response.onboardingModelValidations;
                this._offeringsCount = response.offeringsCount;
                this._offeringsWithVisibleMetaCount = response.offeringsWithVisibleMetaCount;
                this._onboardingRequiredDocumentTypesCount = response.onboardingRequiredDocumentTypesCount;
                this._onboardingModelProperties = response.onboardingModelProperties;
                this._offeringModelProperties = response.offeringModelProperties;
                this._termsAndConditions = response.termsAndConditions;
                this._navigationHandler = response.navigationHandler;
                this._doNotUseFallback = response.doNotUseFallback;
                this._onboardingName = response.onboardingName;
                this._isGdprConsentEnabled = response.isGdprConsentEnabled;
                this._whatsNeededSteps = response.whatsNeededSteps;
                this._entityTypeId = response.entityTypeId;

                resolve();
            }).catch(reject);
        });
    }

    public startListeningToHistoryChanges() {
        window.addEventListener('popstate', this.onPopState, false);
    }

    registerVerificationHandler(verificationHandler: IVerificationHandler) {
        this._verificationHandler = verificationHandler;
    }

    updateSession(onboardingRequestGuid: string) {
        window["onboardingRequestUrlMeta"]["onboardingRequestGuid"] = onboardingRequestGuid;
        this._onboardingRequestGuid = onboardingRequestGuid;
    }

    getOnboardingModelValidations(): RulesHolder {
        return this._onboardingModelValidations;
    }

    getOfferingsCount(): number {
        return this._offeringsCount;
    }

    getOfferingsWithVisibleMetaCount(): number {
        return this._offeringsWithVisibleMetaCount;
    }

    getOnboardingRequiredDocumentTypesCount(): number {
        return this._onboardingRequiredDocumentTypesCount;
    }

    getOfferingsSelected(): OfferingModel[] {
        return this._offerings;
    }

    validateOnSubmit(): boolean {
        return  window["validateOnSubmit"];
    }

    storeDocuments(documents: OnboardingRequestDocumentModel[]) {
        if (!this._documents) {
            this._documents = [];
        }
        this._documents = this._documents.concat(documents);
    }

    getDocuments(): OnboardingRequestDocumentModel[] {
        return this._documents;
    }

    setOfferingsSelected(offerings: OfferingModel[]) {
        this._offerings = offerings;
        this._offeringsBags = this._offerings.map(x => ({ offeringId: x.offeringId, bag: {} }));
    }

    usePrevVerificationOrPrevStep() {
        this._verificationHandler.usePrevVerification();
    }

    useNextVerificationOrNextStep(verificationInternalName: string, meta: any, retriesDone: number, skipNextVerifications: boolean = false): Promise<any> {
        return new Promise<void>((resolve, reject) => {
            this.onboardingClientService.saveVerificationMeta({ metaJson: JSON.stringify(meta), retriesDone, verificationInternalName: verificationInternalName, onboardingRequestGuid: this.onboardingRequestGuid }).then(x => {
                if (!x.saved) {
                    reject();
                    return;
                } else {
                    this.onboardingClientService.getOnboardingRequest().then(onboardingRequest => {
                        const modelMeta = JSON.parse(onboardingRequest.onboardingRequest.modelMeta);
                        this.updateModelMeta(modelMeta);
                        if (!skipNextVerifications) {
                            this._verificationHandler.useNextVerification();
                        } else {
                            this.nextStep(this._handler.currentStep);
                        }
                        resolve();
                    });
                }
            });
        });
    }


    set validationErrorsOnSubmit(value) {
        this._validationErrorsOnSubmit = value;
    }

    @computedFrom(Reflection.getPropertyPath<OnboardingSession>(z => z._validationErrorsOnSubmit))
    get validationErrorsOnSubmit(): boolean {
        return this._validationErrorsOnSubmit;
    }

    @computedFrom(Reflection.getPropertyPath<OnboardingSession>(z => z._isEmbedded))
    get isEmbedded(): boolean {
        return this._isEmbedded;
    }

    @computedFrom(Reflection.getPropertyPath<OnboardingSession>(z => z._onboardingName))
    get onboardingName(): string {
        return this._onboardingName;
    }

    @computedFrom(Reflection.getPropertyPath<OnboardingSession>(z => z._doNotUseFallback))
    get doNotUseFallback() {
        return this._doNotUseFallback;
    }

    @computedFrom(Reflection.getPropertyPath<OnboardingSession>(z => z._handler.currentStep))
    get currentStepIndex() {
        return this._handler.currentStepIndex();
    }

    @computedFrom(Reflection.getPropertyPath<OnboardingSession>(z => z._navigationHandler))
    get navigationHandler() {
        return this._navigationHandler;
    }

    @computedFrom(Reflection.getPropertyPath<OnboardingSession>(z => z._isBasicMetaFormStyle))
    get isBasicMetaFormStyle() {
        return this._isBasicMetaFormStyle;
    }

    @computedFrom(Reflection.getPropertyPath<OnboardingSession>(z => z._verificationsMeta))
    get verificationsMeta() {
        return this._verificationsMeta;
    }

    get isAdvancedMetaFormStyle() {
        return this._isAdvancedMetaFormStyle;
    }

    get pluginName() {
        return this._pluginName;
    }

    @computedFrom(Reflection.getPropertyPath<OnboardingSession>(z => z._onboardingModelProperties))
    get onboardingModelProperties(): PropertyMetaModel[] {
        return this._onboardingModelProperties ? this._onboardingModelProperties.slice(0) : [];
    }

    @computedFrom(Reflection.getPropertyPath<OnboardingSession>(z => z._offeringModelProperties))
    get offeringModelProperties(): PropertyMetaModel[] {
        return this._offeringModelProperties ? this._offeringModelProperties.slice(0) : [];
    }

    @computedFrom(Reflection.getPropertyPath<OnboardingSession>(z => z._onboardingRequestGuid))
    get onboardingRequestGuid(): string {
        return this._onboardingRequestGuid;
    }

    @computedFrom(Reflection.getPropertyPath<OnboardingSession>(z => z._basicMetaConfig))
    get basicMetaConfig() {
        return this._basicMetaConfig;
    }

    @computedFrom(Reflection.getPropertyPath<OnboardingSession>(z => z._advancedMetaConfig))
    get advancedMetaConfig() {
        return this._advancedMetaConfig;
    }

    @computedFrom(Reflection.getPropertyPath<OnboardingSession>(z => z._pluginMetaConfig))
    get pluginMetaConfig() {
        return this._pluginMetaConfig;
    }

    @computedFrom(Reflection.getPropertyPath<OnboardingSession>(z => z._offeringsMetaConfig))
    get offeringsMetaConfig() {
        return this._offeringsMetaConfig;
    }

    @computedFrom(Reflection.getPropertyPath<OnboardingSession>(z => z._termsAndConditions))
    get termsAndConditions() {
        return this._termsAndConditions;
    }

    @computedFrom(Reflection.getPropertyPath<OnboardingSession>(z => z._isGdprConsentEnabled))
    get isGdprConsentEnabled() {
        return this._isGdprConsentEnabled;
    }

    @computedFrom(Reflection.getPropertyPath<OnboardingSession>(z => z._landingPage))
    get landingPage() {
        return this._landingPage;
    }

    @computedFrom(Reflection.getPropertyPath<OnboardingSession>(z => z._onboardingRequestsPath))
    get onboardingRequestsPath() {
        return this._onboardingRequestsPath;
    }

    @computedFrom(Reflection.getPropertyPath<OnboardingSession>(z => z._completeRequestLandingPage))
    get completeRequestLandingPage() {
        return this._completeRequestLandingPage;
    }

    @computedFrom(Reflection.getPropertyName<OnboardingSession>(os => os._whatsNeededSteps))
    get whatsNeededSteps() {
        return this._whatsNeededSteps;
    }

    @computedFrom(Reflection.getPropertyName<OnboardingSession>(os => os._entityTypeId))
    get entityTypeId() {
        return this._entityTypeId;
    }

    get query(): string {
        if (!this._query) {
            this._query = window.location.href;
        }
        return this._query;
    }

    get guid(): string {
        if (!this._guid) {
            this._guid =  window["onboardingGuid"] || location.pathname.replace("/OnboardingClient/", "");
        }
        return this._guid;
    }

    @computedFrom(Reflection.getPropertyPath<OnboardingSession>(z => z._modelMeta))
    get modelMeta() {
        return this._modelMeta;
    }

    get offeringsMetaBags(): { offeringId: number, bag: any }[] {
        return this._offeringsBags;
    }

    destroySession() {
        this._modelMeta = null;
        this._handler = null;
    }

    canCompleteSession(): boolean {
        const currentIndex = this._handler.currentStepIndex();
        const prelastIndex = (this._handler.countSteps() - 2);
        return currentIndex == prelastIndex && Object.keys(this._handler.visibleSteps[prelastIndex + 1].navigationPaths).length == 0;
    }

    rejectSession() {
        return new Promise<void>((resolve) => {
            this.onboardingClientService.rejectOnboardingRequest({ onboardingRequestGuid: this.onboardingRequestGuid }).then(() => {
                resolve();
            });
        });
    }

    updateModelMeta(modelMeta: any) {
        //this._modelMeta = $.extend({}, this._modelMeta, modelMeta);
        $.extend(this._modelMeta, modelMeta)
    }

    completeSession(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            if (this._handler.currentStepIndex() == (this._handler.countSteps() - 2)) {
                this.onboardingClientService.completeOnboardingRequest({ onboardingRequestGuid: this.onboardingRequestGuid }).then(x => {
                    if (x.isCompleted && (x.validationItems == null || x.validationItems.length == 0)) {
                        if (this._handler && this._handler.goToStep) {
                            this._handler.goToStep(this._handler.currentStepIndex() + 1, null).then(() => {
                                window.removeEventListener('popstate', this.onPopState, false);
                                resolve();
                            }).catch(reject);
                        }
                        resolve();
                    }
                    reject(x);
                }).catch(() => {
                    reject();
                });
            } else {
                reject();
            }
        });
    }

    nextStep(requesterViewModel: BaseOnboardingStepModel, navigationPathKey: string = null) {
        if (this._handler.currentStep != requesterViewModel) {
            return;
        }
        return new Promise<void>((resolve, reject) => {
            if (this.canCompleteSession()) {
                this.completeSession().then(() => {
                    this.onboardingClientService.getOnboardingRequest().then(onboardingRequest => {
                        const modelMeta = JSON.parse(onboardingRequest.onboardingRequest.modelMeta);
                        this.updateModelMeta(modelMeta);
                        resolve();
                    });
                }).catch(reject);
            } else {
                if (this._handler && this._handler.goToStep) {
                    const newIndex = this._handler.currentStepIndex() + 1;
                    this._handler.goToStep(newIndex, navigationPathKey).then(() => {
                        const newUrl = UrlHelper.updateURL({
                            'step': newIndex
                        }, ['page']);
                        this.pushUrl(newUrl);
                        resolve();
                    }).catch(reject);
                } else {
                    reject();
                }
            }
        });
    }

    prevStep() {
        if (this._handler) {
            history.back();
            this.popUrl();
        }
    }

    goToStep(index: number) {
        if (this._handler && this._handler.goToStep) {
            const newUrl = UrlHelper.updateURL({
                'step': index
            }, ['page']);
            this.pushUrl(newUrl);
        }
    }

    pushUrl(newUrl: string) {
        if(this._urls.indexOf(newUrl) === -1){
            this._urls.push(newUrl);
            if (this.onboardingRequestGuid) {
                this.onboardingClientService.saveUrlsHistory(this.onboardingRequestGuid, this._urls);
            }
        }
    }

    popUrl() {
        this._urls.pop();
        if (this.onboardingRequestGuid) {
            this.onboardingClientService.saveUrlsHistory(this.onboardingRequestGuid, this._urls);
        }
    }

    getUrlsHistory() {
        return this._urls.slice(0);
    }

    onPopState = () => {
        const currentStep = UrlHelper.getParameterValueByKey('step');

        if (currentStep != this._handler.currentStepIndex().toString()) {
            this._handler.goToStep(Number(currentStep || "0"), null);
        }
    }

    destroy() {
        window.removeEventListener('popstate', this.onPopState, false);
    }
}
